import { TextInput, ReferenceInput, required, maxLength } from "react-admin";
import { Alert, Grid } from "@mui/material";
import { employeeRequests } from "api/employeeApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { validateStart, validateEnd } from "utils/validations";
import { DormitoryCampaignApplicantTypeChoices } from "utils/choices";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";

const NAME_MAX_LENGTH = 200;

const STRUCTURAL_LOCK_MESSAGE =
  "Аудиторията и датите за настаняване не могат да се променят, след като по кампанията вече има подадени молби.";

const userOptionText = rec =>
  rec.secondName ? `${rec.firstName} ${rec.secondName} ${rec.lastName}` : `${rec.firstName} ${rec.lastName}`;

// mode: "create" | "edit" — the defensive edit locking must never leak into create.
// readOnly: whole form is read-only (terminal campaign — historical visibility only).
// structuralLocked: only the three structural fields (кандидати / дати за настаняване) are locked,
// because dependent applications already exist.
const SaveForm = ({ mode = "create", readOnly = false, structuralLocked = false }) => {
  const isEdit = mode === "edit";
  const descriptiveReadOnly = isEdit && readOnly;
  const structuralReadOnly = isEdit && (readOnly || structuralLocked);
  const showStructuralNotice = isEdit && !readOnly && structuralLocked;

  const validateDocumentsFrom = (value, allValues) => validateStart(value, allValues.documentsTo, true);
  const validateDocumentsTo = (value, allValues) => validateEnd(value, allValues.documentsFrom, true);
  const validateAccommodationFrom = (value, allValues) => validateStart(value, allValues.accommodationTo, true);
  const validateAccommodationTo = (value, allValues) => validateEnd(value, allValues.accommodationFrom, true);

  return (
    <Grid
      container
      spacing={2}
    >
      {showStructuralNotice && (
        <Grid
          item
          xs={12}
        >
          <Alert severity="info">{STRUCTURAL_LOCK_MESSAGE}</Alert>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameBg"
          label="Име на български"
          fullWidth
          readOnly={descriptiveReadOnly}
          validate={[required(), maxLength(NAME_MAX_LENGTH)]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameEn"
          label="Име на английски"
          fullWidth
          readOnly={descriptiveReadOnly}
          validate={[required(), maxLength(NAME_MAX_LENGTH)]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="documentsFrom"
          label="Начало на подаване на документи"
          readOnly={descriptiveReadOnly}
          validate={[required(), validateDocumentsFrom]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="documentsTo"
          label="Край на подаване на документи"
          readOnly={descriptiveReadOnly}
          validate={[required(), validateDocumentsTo]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="commissionChairEmployeeId"
          reference={employeeRequests.getEmployeesSelect()}
        >
          <DormitoryAutocompleteInput
            label="Председател на комисията"
            fullWidth
            optionText={userOptionText}
            readOnly={descriptiveReadOnly}
            validate={[required()]}
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="sessionDate"
          label="Дата на заседание"
          readOnly={descriptiveReadOnly}
          validate={[required()]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="accommodationFrom"
          label="Начало на настаняване"
          readOnly={structuralReadOnly}
          validate={[required(), validateAccommodationFrom]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="accommodationTo"
          label="Край на настаняване"
          readOnly={structuralReadOnly}
          validate={[required(), validateAccommodationTo]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
        sx={{ mx: "auto" }}
      >
        <DormitoryAutocompleteInput
          source="applicantType"
          label="Кандидати"
          choices={DormitoryCampaignApplicantTypeChoices}
          readOnly={structuralReadOnly}
          validate={[required()]}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default SaveForm;
